/**
 * LivrePanel — LOT 5 : livre de comptabilité unifié (entrées / sorties).
 * Journal chronologique fusionnant encaissements et décaissements, soldes de
 * période, filtres de dates, export CSV. Onglet « Livre » de la brique compta.
 */
import { useCallback, useEffect, useState , type ReactNode } from 'react';
import { Card, Button, Alert } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { LivreResult } from '@/lib/api';
import { ArrowDownRight, ArrowUpRight, Download, RefreshCw } from 'lucide-react';

// Wrapper léger (le composant Field du design-system impose htmlFor).
function FieldBox({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-medium text-ink-muted">{label}</label>
      {children}
    </div>
  );
}


function euro(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function frDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}
function premierDuMois(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export function LivrePanel() {
  const [livre, setLivre] = useState<LivreResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [du, setDu] = useState(premierDuMois());
  const [au, setAu] = useState(() => new Date().toISOString().slice(0, 10));

  const charger = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.comptabilite.livre.get(du || undefined, au || undefined);
      setLivre(r);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Impossible de charger le livre.');
    } finally { setLoading(false); }
  }, [du, au]);

  useEffect(() => { void charger(); }, [charger]);

  async function exporter() {
    try { await api.comptabilite.livre.exportCsv(du || undefined, au || undefined); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Export impossible.'); }
  }

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <Card variant="glass" padded>
        <div className="flex flex-wrap items-end gap-3">
          <FieldBox label="Du" className="w-40">
            <input type="date" value={du} onChange={(e) => setDu(e.target.value)}
              className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
          </FieldBox>
          <FieldBox label="Au" className="w-40">
            <input type="date" value={au} onChange={(e) => setAu(e.target.value)}
              className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
          </FieldBox>
          <Button variant="ghost" onClick={() => void charger()}><RefreshCw className="mr-1 h-4 w-4" /> Actualiser</Button>
          <Button variant="compta" onClick={() => void exporter()}><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
        </div>
      </Card>

      {/* Soldes de période */}
      {livre && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card variant="glass" padded>
            <div className="text-xs text-ink-subtle">Entrées</div>
            <div className="mt-1 flex items-center gap-1 text-lg font-semibold tabular-nums text-success">
              <ArrowDownRight className="h-4 w-4" /> {euro(livre.totalEntrees)}
            </div>
          </Card>
          <Card variant="glass" padded>
            <div className="text-xs text-ink-subtle">Sorties</div>
            <div className="mt-1 flex items-center gap-1 text-lg font-semibold tabular-nums text-danger">
              <ArrowUpRight className="h-4 w-4" /> {euro(livre.totalSorties)}
            </div>
          </Card>
          <Card variant="glass" padded>
            <div className="text-xs text-ink-subtle">Solde de la période</div>
            <div className="mt-1 text-lg font-semibold tabular-nums text-ink">{euro(livre.solde)}</div>
          </Card>
        </div>
      )}

      <Card className="overflow-hidden p-0">
        <div className="border-b border-border px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">
            Journal {livre ? `· ${livre.nbOperations} opération${livre.nbOperations > 1 ? 's' : ''}` : ''}
          </h2>
        </div>
        {loading ? (
          <div className="py-10 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
              style={{ borderColor: 'oklch(var(--c-compta))', borderTopColor: 'transparent' }} role="status" aria-label="Chargement" />
          </div>
        ) : !livre || livre.lignes.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-subtle">Aucune opération sur la période sélectionnée.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-ink-subtle">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Objet</th>
                  <th className="px-3 py-2 text-left font-medium">Tiers</th>
                  <th className="px-3 py-2 text-left font-medium">Moyen</th>
                  <th className="px-3 py-2 text-right font-medium">Entrée</th>
                  <th className="px-3 py-2 text-right font-medium">Sortie</th>
                </tr>
              </thead>
              <tbody>
                {livre.lignes.map((l) => (
                  <tr key={`${l.sens}-${l.id}`} className="border-b border-border/60">
                    <td className="whitespace-nowrap px-3 py-2 text-ink-muted">{frDate(l.date)}</td>
                    <td className="px-3 py-2 text-ink">{l.objet}</td>
                    <td className="px-3 py-2 text-ink-muted">{l.tiers ?? '—'}</td>
                    <td className="px-3 py-2 text-ink-subtle">{l.moyen}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-success">{l.sens === 'entree' ? euro(l.montant) : ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-danger">{l.sens === 'sortie' ? euro(l.montant) : ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td colSpan={4} className="px-3 py-2 text-right text-ink">Totaux</td>
                  <td className="px-3 py-2 text-right tabular-nums text-success">{euro(livre.totalEntrees)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-danger">{euro(livre.totalSorties)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
